import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TLessonBase } from '@onesource/schemas';
import { LessonStatus } from '@onesource/schemas';
import { BsChevronRight } from 'react-icons/bs';
import { DraftBadge } from 'src/components/draft-badge';

import { useOpenLesson } from '../hooks/use-open-lesson';
import { usePreviewMode } from '../hooks/use-preview-mode';

interface ModuleLessonGroupProps {
  categoryName: string;
  lessons: TLessonBase[];
  parentSpaceId: string;
  moduleId: string;
  sectionId: string;
  showLessonLabel?: boolean;
}

/**
 * Module Lesson Group Component
 * Displays a category header with its lessons
 * Based on Figma design
 */
export const ModuleLessonGroup = ({
  categoryName,
  lessons,
  parentSpaceId,
  moduleId,
  showLessonLabel = true,
}: ModuleLessonGroupProps) => {
  const { isPreview } = usePreviewMode();
  const handleOpenLesson = useOpenLesson(parentSpaceId, moduleId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
      {/* Category/Module Header */}
      {categoryName && (
        <Typography
          sx={{
            fontFamily: '"Figtree", sans-serif',
            fontWeight: 700,
            fontSize: { xs: 11, md: 12 },
            lineHeight: '17px',
            letterSpacing: '2.64px',
            textTransform: 'uppercase',
            color: '#ff7262',
          }}
        >
          {categoryName}
        </Typography>
      )}

      {/* Lessons List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {lessons.map((lesson, index) => (
          <Box
            key={lesson.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: '2px',
            }}
          >
            {/* Lesson Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: 12, md: 14 },
                  lineHeight: '19px',
                  color: 'black',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => handleOpenLesson(lesson)}
              >
                {showLessonLabel ? `Lesson ${index + 1}: ${lesson.title}` : lesson.title}
              </Typography>
              {isPreview && lesson.status !== LessonStatus.PUBLISHED && <DraftBadge />}
            </Box>

            {/* Open Button */}
            <Box
              onClick={() => handleOpenLesson(lesson)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: 12, md: 12 },
                  lineHeight: '17px',
                  color: '#ff073b',
                }}
              >
                Open
              </Typography>
              <Box
                component="span"
                sx={{
                  color: '#ff073b',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BsChevronRight size={12} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
